import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to Grainmill Operations");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-navy flex flex-col items-center justify-center px-6"
      data-testid="login-page"
    >
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/grainmill-logo.jpg"
            alt="Grainmill"
            className="h-28 mx-auto mb-5 rounded-xl shadow-lg shadow-black/30"
          />
          <h1 className="text-2xl font-heading font-bold text-cream tracking-tight">
            Operations
          </h1>
          <p className="text-cream/40 text-sm mt-1">
            Sign in to your account
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-cream/70 text-xs uppercase tracking-wider">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/25" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="pl-10 bg-white/5 border-cream/10 text-cream placeholder:text-cream/25 h-12 rounded-xl focus:border-gold focus:ring-gold/30"
                data-testid="login-email-input"
                required
                autoComplete="email"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-cream/70 text-xs uppercase tracking-wider">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/25" />
              <Input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="pl-10 pr-10 bg-white/5 border-cream/10 text-cream placeholder:text-cream/25 h-12 rounded-xl focus:border-gold focus:ring-gold/30"
                data-testid="login-password-input"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/25 hover:text-cream/50"
                tabIndex={-1}
              >
                {showPw ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="bg-red-500/10 border border-red-400/30 rounded-lg px-4 py-2.5 text-red-300 text-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full gm-btn-gold text-base mt-2"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>

          <div className="text-center pt-1">
            <button
              type="button"
              className="text-gold/50 hover:text-gold text-xs transition-colors"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </div>
        </form>

        <p className="text-cream/20 text-[10px] text-center mt-10">
          Grainmill Ltd &bull; Indian Groceries &bull; Fresh Produce
        </p>
      </div>
    </div>
  );
}
